import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Etiqueta from "../kit/Etiqueta";
import { usePoblarPaso } from "../../queries/usePasos";
import { usePeriodos } from "../../queries/useEvaluadores";
import { Paso, REGLAS_INGRESO, ResultadoPoblado } from "../../types/paso";
import { mensajeDeError } from "../../utils/errores";
import { SEMESTRES } from "../../utils/semestre";

/**
 * Trae al paso los estudiantes que le corresponden según su regla de ingreso
 * (dry-run → confirmar). Para la regla `inscripcion_curso` eso es preguntarle a
 * UCampus quién inscribió el curso; de ahí el nombre del botón que lo abre.
 *
 * Vive en la vista de Estudiantes —es donde se mira la lista que actualiza—, con
 * `ano`/`semestre` iniciales que puede fijar quien lo abre.
 */
export default function PoblarPasoDialog({
  paso,
  ano: anoInicial,
  semestre: semestreInicial,
  onClose,
}: {
  paso: Paso;
  ano?: number;
  semestre?: number;
  onClose: () => void;
}) {
  const { data: periodos } = usePeriodos();
  const poblar = usePoblarPaso();
  const ultimo = periodos?.[0];
  const [ano, setAno] = useState<number>(anoInicial ?? ultimo?.ano ?? new Date().getFullYear());
  const [semestre, setSemestre] = useState<number>(semestreInicial ?? ultimo?.semestre ?? 1);
  const [preview, setPreview] = useState<ResultadoPoblado | null>(null);
  const [elegidos, setElegidos] = useState<number[]>([]);
  const [darDeBaja, setDarDeBaja] = useState(false);

  const correr = (commit: boolean) =>
    poblar.mutate(
      // Al previsualizar se pide todo el curso —hay que poder ver a quién dejar
      // fuera—; al confirmar manda solo lo marcado.
      { paso: paso.id, ano, semestre, commit, dar_de_baja: darDeBaja, ...(commit ? { ruts: elegidos } : {}) },
      {
        onSuccess: (res) => {
          setPreview(res);
          // Vienen marcados los que no tienen un proceso vigente en otro paso: los
          // otros se traerían duplicados, así que se marcan a mano si se quiere.
          if (!res.publicado) setElegidos(res.creadas.filter((c) => !c.conflicto).map((c) => c.rut));
        },
      },
    );

  // Cambiar de cohorte invalida un preview anterior.
  const cambiar = (fn: () => void) => {
    fn();
    setPreview(null);
    setElegidos([]);
  };

  const alternar = (rut: number) => setElegidos((prev) => (prev.includes(rut) ? prev.filter((r) => r !== rut) : [...prev, rut]));

  const publicado = preview?.publicado;
  const candidatos = preview?.creadas ?? [];
  const conConflicto = candidatos.filter((c) => c.conflicto).length;

  // Lo que se hizo, contando las tres cosas: una cohorte ya cerrada no crea a nadie y
  // aun así cierra decenas de procesos — decir solo "se crearon 0" sería mentir.
  const partes = [
    preview?.creadas.length ? `${preview.creadas.length} ${preview.creadas.length === 1 ? "creada" : "creadas"}` : null,
    preview?.cierres?.length ? `${preview.cierres.length} ${preview.cierres.length === 1 ? "cerrada" : "cerradas"}` : null,
    preview?.bajas?.length ? `${preview.bajas.length} de baja` : null,
  ].filter(Boolean);
  const resumen = partes.length ? `Listo: ${partes.join(", ")}.` : "No hubo nada que actualizar.";

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Actualizar «{paso.nombre}»</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {REGLAS_INGRESO.find((r) => r.value === paso.regla_ingreso)?.ayuda} Elige la cohorte y previsualiza antes de confirmar.
          </Typography>
          <Stack direction="row" spacing={1.5}>
            <TextField
              type="number"
              size="small"
              label="Año"
              value={ano}
              onChange={(e) => cambiar(() => setAno(Number(e.target.value)))}
              sx={{ width: 120 }}
            />
            <TextField
              select
              size="small"
              label="Semestre"
              value={semestre}
              onChange={(e) => cambiar(() => setSemestre(Number(e.target.value)))}
              sx={{ minWidth: 160 }}
            >
              {SEMESTRES.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          {paso.regla_ingreso === "inscripcion_curso" && (
            <FormControlLabel
              control={<Checkbox checked={darDeBaja} onChange={(e) => cambiar(() => setDarDeBaja(e.target.checked))} />}
              label={
                <Typography variant="body2">
                  Dar de baja a quienes ya no están inscritos en el curso
                  <Typography component="span" variant="caption" color="text.secondary" display="block">
                    Quedan como «eliminado» y dejan de verse. No se tocan los que tienen entregas o evaluaciones.
                  </Typography>
                </Typography>
              }
            />
          )}

          {poblar.isError && <Alert severity="error">{mensajeDeError(poblar.error, "No se pudo actualizar el paso.")}</Alert>}

          {preview?.avisos.map((a, i) => (
            <Alert key={i} severity="warning">
              {a}
            </Alert>
          ))}

          {preview && (
            <Box>
              {publicado ? (
                <Alert severity="success" sx={{ mb: 1 }}>
                  {resumen}
                </Alert>
              ) : (
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" flexWrap="wrap" useFlexGap>
                  <Typography variant="caption" color="text.secondary">
                    {candidatos.length === 0
                      ? "No hay estudiantes nuevos para este paso en la cohorte."
                      : `Elige a quiénes incluir (${elegidos.length} de ${candidatos.length}):`}
                  </Typography>
                  {candidatos.length > 0 && (
                    <Button
                      size="small"
                      onClick={() => setElegidos(elegidos.length === candidatos.length ? [] : candidatos.map((c) => c.rut))}
                    >
                      {elegidos.length === candidatos.length ? "Ninguno" : "Todos"}
                    </Button>
                  )}
                </Stack>
              )}

              {/* Quien ya está haciendo otro paso de la misma cohorte (típicamente
                  Vía Rápida) sigue inscrito en el curso: incluirlo lo duplicaría. */}
              {!publicado && conConflicto > 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {conConflicto === 1
                    ? "1 estudiante ya tiene un proceso vigente en otro paso y viene sin marcar."
                    : `${conConflicto} estudiantes ya tienen un proceso vigente en otro paso y vienen sin marcar.`}{" "}
                  Incluirlos les dejaría dos procesos abiertos.
                </Alert>
              )}

              {candidatos.length > 0 &&
                (publicado ? (
                  <Stack sx={{ mt: 1, maxHeight: 260, overflowY: "auto" }} divider={<Divider flexItem />}>
                    {candidatos.map((c) => (
                      <Stack key={c.rut} direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
                        <Typography variant="body2">{c.nombre}</Typography>
                        <Etiqueta variante="contorno">{c.origen}</Etiqueta>
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  <Stack sx={{ mt: 0.5, maxHeight: 260, overflowY: "auto" }} divider={<Divider flexItem />}>
                    {candidatos.map((c) => (
                      <FormControlLabel
                        key={c.rut}
                        control={<Checkbox size="small" checked={elegidos.includes(c.rut)} onChange={() => alternar(c.rut)} />}
                        label={
                          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography variant="body2">{c.nombre}</Typography>
                            <Etiqueta variante="contorno">{c.origen}</Etiqueta>
                            {c.conflicto && (
                              <Etiqueta variante="acento">
                                ya está en {c.conflicto.paso} ({c.conflicto.estado})
                              </Etiqueta>
                            )}
                          </Stack>
                        }
                        sx={{ py: 0.25, mr: 0 }}
                      />
                    ))}
                  </Stack>
                ))}

              {preview.cierres?.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {publicado
                      ? `Se cerraron ${preview.cierres.length} con el resultado de UCampus:`
                      : `UCampus ya tiene resultado de ${preview.cierres.length}; se cerrarían así:`}
                  </Typography>
                  <Stack sx={{ mt: 1, maxHeight: 180, overflowY: "auto" }} divider={<Divider flexItem />}>
                    {preview.cierres.map((c) => (
                      <Stack key={c.rut} direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
                        <Typography variant="body2">{c.nombre}</Typography>
                        <Etiqueta>{c.origen}</Etiqueta>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )}

              {preview.bajas?.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {publicado
                      ? `Se dieron de baja ${preview.bajas.length}:`
                      : `Se darían de baja ${preview.bajas.length} que ya no están en el curso:`}
                  </Typography>
                  <Stack sx={{ mt: 1, maxHeight: 180, overflowY: "auto" }} divider={<Divider flexItem />}>
                    {preview.bajas.map((b) => (
                      <Stack key={b.rut} direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
                        <Typography variant="body2">{b.nombre}</Typography>
                        <Etiqueta>{b.origen}</Etiqueta>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Button onClick={onClose}>{publicado ? "Cerrar" : "Cancelar"}</Button>
        {!publicado && (
          <Stack direction="row" spacing={1}>
            <Button onClick={() => correr(false)} disabled={poblar.isPending}>
              {poblar.isPending ? "Calculando…" : "Previsualizar"}
            </Button>
            <Button
              variant="contained"
              onClick={() => correr(true)}
              disabled={
                poblar.isPending || !preview || (elegidos.length === 0 && !preview.bajas?.length && !preview.cierres?.length)
              }
            >
              Confirmar
            </Button>
          </Stack>
        )}
      </DialogActions>
    </Dialog>
  );
}
